/**
 * COMPACT CHROME (ROOMS.md §2.1) — one remembered-on-this-machine choice: the top bar, the room header and the room
 * tabs collapse into ONE dock row of icons (for experienced users), or show in full. Every component that reads it stays
 * in step (same-tab event + cross-tab ``storage``). Default: full (new users get the guidance).
 */
import { useCallback, useEffect, useState } from 'react';

export const COMPACT_CHROME_KEY = 'stepflow:compact-chrome';
const EVT = 'stepflow:compact-chrome';
/** The dock row's slots the room fills (React portals) — its header group and its tab icons. */
export const DOCK_ROOM_SLOT_ID = 'stepflow-dock-room-slot';
export const DOCK_TABS_SLOT_ID = 'stepflow-dock-tabs-slot';
/** ONE fixed height for the dock row AND the sidebar's top block, so their bottom borders line up (40 px). */
export const DOCK_ROW_HEIGHT = 'h-10';

export function readCompactChrome() {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(COMPACT_CHROME_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export default function useCompactChrome() {
  const [compact, setCompactState] = useState(readCompactChrome);
  useEffect(() => {
    const sync = () => setCompactState(readCompactChrome());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(EVT, sync); window.removeEventListener('storage', sync); };
  }, []);
  const setCompact = useCallback((v) => {
    try { window.localStorage?.setItem(COMPACT_CHROME_KEY, v ? '1' : '0'); } catch (_e) { /* private mode */ }
    setCompactState(!!v);
    window.dispatchEvent(new Event(EVT));
  }, []);
  return [compact, setCompact];
}

/** The dock's slot elements once they exist (null when not compact or no dock on this page). */
export function useDockSlots(compact) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!compact) { setSlots(null); return undefined; }
    let raf = 0;
    let tries = 0;
    const find = () => {
      const room = document.getElementById(DOCK_ROOM_SLOT_ID);
      const tabs = document.getElementById(DOCK_TABS_SLOT_ID);
      if (room && tabs) setSlots({ room, tabs });
      else if (tries++ < 60) raf = requestAnimationFrame(find);   // ~1 s — no dock on this page → full header
    };
    find();
    return () => cancelAnimationFrame(raf);
  }, [compact]);
  // the dock re-rendered its slots (new DOM nodes) → re-attach, so the room's header/tabs never vanish
  useEffect(() => {
    if (!compact) return undefined;
    const obs = new MutationObserver(() => {
      setSlots((cur) => {
        if (cur && cur.room.isConnected && cur.tabs.isConnected) return cur;
        const room = document.getElementById(DOCK_ROOM_SLOT_ID);
        const tabs = document.getElementById(DOCK_TABS_SLOT_ID);
        return room && tabs ? { room, tabs } : null;
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [compact]);
  return slots;
}
